/**
 * The single place the web client talks to the platform.
 *
 * Responsibilities kept here rather than in components:
 *  - token storage and transparent refresh on 401
 *  - the active viewer profile header
 *  - one error shape, matching the API's error envelope
 *  - trace correlation, so a browser error can be found in the backend trace
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";
const ACCESS_KEY = "hayfish.access";
const REFRESH_KEY = "hayfish.refresh";
const PROFILE_KEY = "hayfish.profile";

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details: Record<string, unknown> = {},
    public traceId?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  get isAuthError(): boolean {
    return this.status === 401 || this.code === "authentication_failed";
  }

  get isEntitlementError(): boolean {
    return this.status === 402 || this.code === "entitlement_required";
  }

  /**
   * Too many streams at once - a different problem from a lapsed subscription.
   * Audit finding F-60: the API used to report this as 402, so a viewer whose
   * own stale sessions had locked them out was shown a billing message.
   */
  get isConcurrencyError(): boolean {
    return this.status === 409 || this.code === "concurrent_stream_limit";
  }

  /**
   * The credentials were right but the address was never confirmed.
   *
   * Distinct from a plain 403 because the remedy is a button, not a support
   * ticket - the sign-in form offers to send the link again rather than
   * telling somebody their account is disabled.
   */
  get isUnverifiedEmailError(): boolean {
    return this.code === "email_not_verified";
  }

  /**
   * The first per-field message, when the API sent one.
   *
   * A 400 from a serialiser arrives as `message: "One or more fields failed
   * validation"` with the useful part in `details`, keyed by field. Showing
   * only the envelope message tells somebody who mistyped the captcha nothing
   * about what to fix - and on a form whose challenge is spent on every
   * attempt, "try again" without "the characters did not match" is the
   * difference between one more try and giving up.
   */
  get fieldMessage(): string | null {
    for (const value of Object.values(this.details)) {
      if (typeof value === "string" && value.trim()) return value;
      if (Array.isArray(value)) {
        const first = value.find((entry) => typeof entry === "string" && entry.trim());
        if (first) return String(first);
      }
    }
    return null;
  }
}

export const tokens = {
  access: (): string | null => localStorage.getItem(ACCESS_KEY),
  refresh: (): string | null => localStorage.getItem(REFRESH_KEY),
  profile: (): string | null => localStorage.getItem(PROFILE_KEY),
  set(access: string, refresh?: string) {
    localStorage.setItem(ACCESS_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  },
  setProfile(profileId: string | null) {
    if (profileId) localStorage.setItem(PROFILE_KEY, profileId);
    else localStorage.removeItem(PROFILE_KEY);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(PROFILE_KEY);
  },
};

type RequestOptions = {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  anonymous?: boolean;
  raw?: boolean;
};

let refreshInFlight: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  const refresh = tokens.refresh();
  if (!refresh) return false;
  // Collapse concurrent refreshes: a page load fires several requests at once
  // and they must not each burn a refresh token.
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/v1/auth/refresh`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ refresh_token: refresh }),
        });
        if (!response.ok) return false;
        const data = await response.json();
        tokens.set(data.access_token, data.refresh_token);
        return true;
      } catch {
        return false;
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  return refreshInFlight;
}

async function send(path: string, options: RequestOptions, retry = true): Promise<Response> {
  const headers: Record<string, string> = { accept: "application/json" };
  if (options.body !== undefined) headers["content-type"] = "application/json";
  if (!options.anonymous) {
    const access = tokens.access();
    if (access) headers.authorization = `Bearer ${access}`;
    const profile = tokens.profile();
    if (profile) headers["x-hayfish-profile"] = profile;
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    method: options.method ?? "GET",
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  });

  if (response.status === 401 && retry && !options.anonymous && (await refreshAccessToken())) {
    return send(path, options, false);
  }
  return response;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options);
  if (options.raw) return response as unknown as T;

  if (!response.ok) {
    let code = `http_${response.status}`;
    let message = response.statusText || "Request failed";
    let details: Record<string, unknown> = {};
    try {
      const payload = await response.json();
      if (payload?.error) {
        code = payload.error.code ?? code;
        message = payload.error.message ?? message;
        details = payload.error.details ?? {};
      }
    } catch {
      /* a non-JSON error body is still an error */
    }
    throw new ApiError(
      response.status,
      code,
      message,
      details,
      response.headers.get("x-trace-id") ?? undefined,
    );
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T,>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T,>(path: string, body?: unknown, options: RequestOptions = {}) =>
    request<T>(path, { ...options, method: "POST", body }),
  patch: <T,>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  delete: <T,>(path: string) => request<T>(path, { method: "DELETE" }),
  anonymousPost: <T,>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body, anonymous: true }),
  anonymousGet: <T,>(path: string) => request<T>(path, { anonymous: true }),
};

/** Server-sent events for streamed assistant replies. */
export async function* streamAssistance(
  message: string,
  conversationId?: string,
  surface = "viewer",
): AsyncGenerator<Record<string, unknown>> {
  const response = await send("/api/v1/ai/assistance", {
    method: "POST",
    body: { message, conversation_id: conversationId ?? "", surface, stream: true },
  });
  if (!response.ok || !response.body) {
    throw new ApiError(response.status, "stream_failed", "The assistant is unavailable");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const frames = buffer.split("\n\n");
    buffer = frames.pop() ?? "";
    for (const frame of frames) {
      const line = frame.trim();
      if (!line.startsWith("data:")) continue;
      try {
        yield JSON.parse(line.slice(5).trim());
      } catch {
        /* ignore partial frames */
      }
    }
  }
}
